import React, { useCallback, useMemo, useState } from "react";

import isHotkey from "is-hotkey";
import { withHistory } from "slate-history";
import { Editable, Slate, withReact } from "slate-react";
import { createEditor, Editor, Range, Transforms } from "slate";

import Leaf from "./components/Leaf";
import PipeElement from "../PipeElement";
import Element from "./components/Element";
import { toggleMark } from "./utils/utility";
import { withImages } from "./utils/imageUtils";
import MarkButton from "./components/MarkButton";
import BlockButton from "./components/BlockButton";
import HeadingFormat from "./components/HeadingFormat";
import InsertImageButton from "./components/InsertImageButton";
import { DOUBLE_ENTER_TIME, HOTKEYS, LIST_TYPES } from "./constants";
import {
  withHtml,
  serialize,
  deserialize,
  cleanUpListItems,
} from "./utils/editorUtils";

const SlateEditor = ({
  name,
  setFieldValue,
  oldValue,
  placeholder,
  minHeight = 100,
  maxHeight = 700,
  customClassNames = [],
  module = "",
}) => {
  const combineClassNames = [
    "form-control ",
    " border-0 ",
    " border-top ",
    "rounded-0",
    ...customClassNames,
  ].join(" ");

  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);
  const renderElement = useCallback((props) => <Element {...props} />, []);

  const editor = useMemo(
    () => withImages(withHtml(withReact(withHistory(createEditor())))),
    [],
  );

  const initialValue = useMemo(() => {
    const tmpvalue = oldValue;

    if (tmpvalue) {
      const doc = new DOMParser().parseFromString(tmpvalue, "text/html");
      const body = doc.body;

      return deserialize(body);
    }

    return [
      {
        type: "paragraph",
        children: [{ text: "" }],
      },
    ];
  }, [oldValue]);

  const [value, setValue] = useState(initialValue);
  const [lastEnterTime, setLastEnterTime] = useState(0);

  const handleOnPaste = async (event, _, next) => {
    // if copied content is not HTML, paste it as plain text otherwise carry the logic below
    event.preventDefault();
    const clipboardHTML = await navigator.clipboard.readText();

    //check if the pasted content is html or plain text using regex
    const htmlRegex =
      /<(?=.*? .*?\/ ?>|br|hr|input|!--|wbr)[a-z]+.*?>|<([a-z]+).*?<\/\1>/i;
    if (!htmlRegex.test(clipboardHTML)) return editor.insertText(clipboardHTML);

    const document = new DOMParser().parseFromString(
      clipboardHTML,
      "text/html",
    );

    cleanUpListItems(document);

    const fragment = deserialize(document.body);

    const removeNewLine = (nodes) => {
      if (!Array.isArray(nodes)) return [];

      return nodes
        .map((node) => {
          if (node.children) {
            node.children = removeNewLine(node.children);
          }
          return node;
        })
        .filter((node) => !(node.text && node.text.includes("\n")));
    };

    const fragmentRemovedNewLine = removeNewLine(fragment);

    const removeBrokenFragment = (nodes) => {
      if (!Array.isArray(nodes)) return [];

      return nodes
        .map((node) => {
          if (node.children) {
            node.children = removeBrokenFragment(node.children);
          }
          return node;
        })
        .filter(
          (node) => node.hasOwnProperty("text") || node.children.length > 0,
        );
    };

    const removeEmptyText = removeBrokenFragment(fragmentRemovedNewLine);

    editor.insertFragment(removeNewLine(removeEmptyText));
  };

  const handleOnKeyDown = (event) => {
    for (const hotkey in HOTKEYS) {
      if (isHotkey(hotkey, event)) {
        event.preventDefault();
        const mark = HOTKEYS[hotkey];
        toggleMark(editor, mark);
      }
    }

    const key = event.key || event.which || event.keyCode;
    if (key === "Enter" || key === 13) {
      const currentTime = new Date().getTime();
      const { selection } = editor;

      if (selection && Range.isCollapsed(selection)) {
        const [match] = Editor.nodes(editor, {
          match: (n) => LIST_TYPES.includes(n.type),
        });

        if (match) {
          if (currentTime - lastEnterTime < DOUBLE_ENTER_TIME) {
            event.preventDefault();

            // Break out of the list item by converting it to a paragraph
            Transforms.unwrapNodes(editor, {
              match: (n) => LIST_TYPES.includes(n.type),
              split: true,
            });

            // Insert a new paragraph node
            Transforms.setNodes(editor, { type: "paragraph" });

            setLastEnterTime(0); // Reset the lastEnterTime to avoid immediate subsequent triggers
            return;
          } else {
            setLastEnterTime(currentTime);
          }
        }
        if (module !== "reports") {
          event.preventDefault();
          Transforms.insertText(editor, "\n");
        }
      }
    } else {
      setLastEnterTime(0);
    }
  };

  return (
    <Slate
      editor={editor}
      initialValue={initialValue}
      value={value}
      onChange={(newValue) => {
        setValue(newValue);
        setFieldValue(name, serialize(editor));
      }}
    >
      <div className="d-flex flex-row flex-wrap align-items-center justify-content-start p-2">
        <HeadingFormat />
        <PipeElement />
        <MarkButton format="bold" icon="format_bold" />
        <MarkButton format="italic" icon="format_italic" />
        <MarkButton format="underline" icon="format_underlined" trim={true} />
        <PipeElement />
        <BlockButton format="numbered-list" icon="format_list_numbered" />
        <BlockButton
          format="bulleted-list"
          icon="format_list_bulleted"
          trim={true}
        />
        <PipeElement />
        <BlockButton format="left" icon="format_align_left" />
        <BlockButton format="center" icon="format_align_center" />
        <BlockButton format="right" icon="format_align_right" />
        <BlockButton format="justify" icon="format_align_justify" trim={true} />
        <PipeElement />
        <InsertImageButton />
      </div>
      <Editable
        spellCheck
        translate="no"
        style={{
          minHeight: minHeight,
          maxHeight: maxHeight,
          overflowY: "auto",
        }}
        onPaste={handleOnPaste}
        renderLeaf={renderLeaf}
        placeholder={placeholder}
        onKeyDown={handleOnKeyDown}
        renderElement={renderElement}
        className={combineClassNames}
      />
    </Slate>
  );
};

export default SlateEditor;
